import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertTriangle, Info, XCircle, X } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ id, message, type, duration = 5000, onClose }) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => onClose(id), 300); // Wait for exit animation
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, id, onClose]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => onClose(id), 300);
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-6 h-6" />;
      case 'error':
        return <XCircle className="w-6 h-6" />;
      case 'warning':
        return <AlertTriangle className="w-6 h-6" />;
      case 'info':
        return <Info className="w-6 h-6" />;
    }
  };

  const getStyles = () => {
    switch (type) {
      case 'success':
        return {
          container: 'bg-gradient-to-r from-green-500 to-green-600',
          icon: 'text-white',
          text: 'text-white',
          closeBtn: 'text-white/80 hover:text-white hover:bg-white/20'
        };
      case 'error':
        return {
          container: 'bg-gradient-to-r from-red-500 to-red-600',
          icon: 'text-white',
          text: 'text-white',
          closeBtn: 'text-white/80 hover:text-white hover:bg-white/20'
        };
      case 'warning':
        return {
          container: 'bg-gradient-to-r from-yellow-500 to-orange-500',
          icon: 'text-white',
          text: 'text-white',
          closeBtn: 'text-white/80 hover:text-white hover:bg-white/20'
        };
      case 'info':
        return {
          container: 'bg-gradient-to-r from-blue-500 to-blue-600',
          icon: 'text-white',
          text: 'text-white',
          closeBtn: 'text-white/80 hover:text-white hover:bg-white/20'
        };
    }
  };

  const styles = getStyles();

  return (
    <div
      className={`
        ${styles.container}
        rounded-xl shadow-2xl p-4 mb-3
        transform transition-all duration-300 ease-out
        ${isExiting ? 'translate-x-full opacity-0' : 'translate-x-0 opacity-100'}
        hover:scale-105 hover:shadow-3xl
        min-w-[380px] max-w-md
        backdrop-blur-sm
        ring-2 ring-white/20
      `}
      role="alert"
      style={{
        animation: isExiting ? 'slide-out 0.3s ease-out' : 'slide-in 0.3s ease-out'
      }}
    >
      <div className="flex items-center gap-4">
        <div className={`flex-shrink-0 ${styles.icon}`}>
          {getIcon()}
        </div>
        <div className="flex-1">
          <p className={`text-base font-semibold leading-relaxed ${styles.text} drop-shadow-sm`}>
            {message}
          </p>
        </div>
        <button
          onClick={handleClose}
          className={`flex-shrink-0 ${styles.closeBtn} transition-all p-1.5 rounded-lg`}
          aria-label="Close notification"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default Toast;

